import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../configs/api";

// Skeleton Card
const BlogCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="w-full h-52 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-full" />
      <div className="h-5 bg-gray-200 rounded w-4/5" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-8 bg-gray-200 rounded-lg w-28 mt-2" />
    </div>
  </div>
);

const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/blogs/");

      const data = res.data?.data?.blogs || [];

      setBlogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, "") || "";

  // Filter
  const filtered = blogs.filter(
    (b) =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      stripHtml(b.description).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <div className="w-full bg-white px-4 md:px-8 lg:px-10 py-16">
        {/* Header */}
        {/* PAGE TITLE */}
        <div className="text-center mb-14">
          <span
            className="text-xs font-medium tracking-widest uppercase"
            style={{ color: "#c020d0", letterSpacing: "0.2em" }}
          >
            [OUR BLOGS]
          </span>

          <h1 className="text-3xl md:text-4xl lg:text-5xl text-[#041122] font-heading font-semibold mt-4">
            Explore Our Blogs
          </h1>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-500">{error}</p>
            <button
              onClick={fetchBlogs}
              className="mt-4 px-6 py-2 bg-btnColor text-white rounded-xl text-sm hover:bg-white hover:text-btnColor border border-btnColor transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">📝</span>
            <p className="text-gray-600 font-medium text-lg">No blogs found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search
                ? "Try a different search term"
                : "No blogs published yet"}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-3 px-5 py-1.5 bg-btnColor text-white rounded-lg text-xs"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* ── Blog Grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogs.map((item) => (
              <div
                key={item._id}
                onClick={() => navigate(`/blog/${item._id}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:border-pink-400 hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                {/* Image */}
                <div className="relative overflow-hidden p-5 border border-gray-200 h-52">
                  <img
                    src={item.images?.[0]}
                    alt={item.title}
                    className="w-full h-full object-cover rounded-xl group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = "https://placehold.co/600x300?text=Blog";
                    }}
                  />
                  {/* Corner Badge */}
                  {/* <div className="absolute top-0 right-0 w-0 h-0 border-t-[55px] border-t-[#92AD36] border-l-[55px] border-l-transparent">
                    <TfiPencil
                      size={15}
                      className="text-white absolute -top-11 right-1.5"
                    />
                  </div> */}
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Date */}
                  {/* <div className="flex items-center gap-2 text-xs text-[#9DABAF] mb-3">
                    <MdOutlineDateRange size={13} />
                    {formatDate(item.publishedDate || item.createdAt)}
                  </div> */}

                  {/* Title */}
                  <h3 className="text-base font-bold text-black line-clamp-2 leading-snug  transition-colors duration-200">
                    {item.name}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-sm text-gray-500 tracking-wide mt-2 line-clamp-3 leading-relaxed">
                    {stripHtml(item.description)}
                  </p>

                  {/* Read More */}
                  {/* <button className="text-[#3A7138] text-xs font-semibold hover:text-[#92AD36] transition flex items-center gap-1">
                    Read More <span className="text-base">→</span>
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {/* {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                  currentPage === i + 1
                    ? "bg-[#92AD36] text-white"
                    : "border border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => {
                setCurrentPage((p) => Math.min(p + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next →
            </button>
          </div>
        )} */}

        {/* Results count */}
        {/* {!loading && filtered.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Showing{" "}
            <span className="font-semibold text-gray-600">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-600">
              {filtered.length}
            </span>{" "}
            blogs
          </p>
        )} */}
      </div>
    </>
  );
};

export default Blogs;
